import React, { useState, useEffect, useCallback } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  PeopleAlt as GroupsIcon,
  PersonAdd as PersonAddIcon,
  Forum as ForumIcon,
  EmojiEvents as EmojiEventsIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Directions as DirectionsIcon,
  Map as MapIcon,
  FilterList as FilterListIcon,
} from "@material-ui/icons";
import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../TableRowSkeleton";
import RouteModal from "../RouteModal";
import MapSelectionModal from "../MapSelectionModal";

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "andamento", label: "Em andamento" },
  { value: "ganho", label: "Ganho" },
  { value: "perdido", label: "Perdido" },
];

const DISTRIBUTION_COLORS = {
  emAndamento: "#f9a825",
  ganhos: "#2e7d32",
  perdidos: "#c62828",
};

const DISTRIBUTION_LABELS = {
  emAndamento: "Em andamento",
  ganhos: "Ganhos",
  perdidos: "Perdidos",
};

// Extrai o bairro do texto salvo em "Endereço" (formato fixo montado pelo
// buildAddressLine do CompanySearchPanel: rua - [complemento] - bairro -
// cidade/UF - CEP). O bairro e' sempre o pedaco logo antes do cidade/UF
// (reconhecido pela barra "/"), tenha complemento ou nao - pedido do
// usuario 2026-09-18 pra identificar/selecionar leads por bairro na
// tabela "Prospects cadastrados" do Painel.
const parseBairro = (address) => {
  if (!address) return null;
  const parts = address.split(" - ").map((p) => p.trim());
  const cityStateIndex = parts.findIndex((p) => p.includes("/"));
  return cityStateIndex >= 2 ? parts[cityStateIndex - 1] : null;
};

const KpiCard = ({ icon, value, label, sublabel, color }) => (
  <Paper variant="outlined" style={{ padding: 16, height: "100%" }}>
    <Box display="flex" alignItems="center" gridGap={8} mb={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ width: 32, height: 32, borderRadius: 8, background: `${color}22`, color }}
      >
        {icon}
      </Box>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
    </Box>
    <Typography variant="h5" style={{ fontWeight: "bold" }}>
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {sublabel}
    </Typography>
  </Paper>
);

// Painel comercial (KPIs + funil + distribuição + lista de prospects) que
// fica acima do quadro Kanban, numa aba separada ("Painel") - pedido do
// usuario 2026-09-15 a partir de um print de outra ferramenta. So aparece
// pra boards com pelo menos uma fase marcada isWonColumn/isLostColumn
// (ver Boards/index.js), senao nao faz sentido nenhum board de suporte
// mostrar "conversao"/"ganhos".
const CommercialDashboard = ({ boardId, phases, users, onOpenCard, onAddProspect, onGoToSearch }) => {
  const theme = useTheme();
  // Tabela "Prospects cadastrados" sem overflow-x vazava da tela em
  // celular (auditoria mobile 2026-09-19) - abaixo do breakpoint "sm"
  // vira lista de cards, e os filtros ficam escondidos atras de um
  // botao (senao a Grid de 6 campos empilhada ocupa a tela toda antes
  // de mostrar qualquer prospect).
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const today = moment().format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [responsavelId, setResponsavelId] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [stats, setStats] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [bairroFilter, setBairroFilter] = useState("");

  const phaseById = React.useMemo(() => {
    const map = {};
    (phases || []).forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [phases]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get(`/boards/${boardId}/commercial-stats`, {
        params: { startDate, endDate, responsavelId: responsavelId || undefined },
      });
      setStats(data);
    } catch (err) {
      toastError(err);
    }
  }, [boardId, startDate, endDate, responsavelId]);

  const fetchProspects = useCallback(async () => {
    setLoadingProspects(true);
    setSelectedCardIds([]);
    try {
      const { data } = await api.get("/cards", {
        params: {
          boardId,
          startDate,
          endDate,
          responsavelId: responsavelId || undefined,
          status: status || undefined,
          search: search || undefined,
        },
      });
      setProspects(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingProspects(false);
    }
  }, [boardId, startDate, endDate, responsavelId, status, search]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    const t = setTimeout(fetchProspects, 400);
    return () => clearTimeout(t);
  }, [fetchProspects]);

  const kpis = stats?.kpis || { total: 0, novos: 0, emAndamento: 0, ganhos: 0, conversao: 0 };
  const funnel = stats?.funnel || [];
  const distribution = stats?.distribution || { emAndamento: 0, ganhos: 0, perdidos: 0 };
  const distributionTotal = distribution.emAndamento + distribution.ganhos + distribution.perdidos;
  const funnelMax = Math.max(1, ...funnel.map((f) => f.count));

  const donutData = ["emAndamento", "ganhos", "perdidos"]
    .filter((key) => distribution[key] > 0)
    .map((key) => ({ name: DISTRIBUTION_LABELS[key], value: distribution[key], key }));

  const getFieldValue = (card, fieldName) => {
    const fv = (card.fieldValues || []).find(
      (f) => (f.customFieldDefinition?.name || "").toLowerCase() === fieldName.toLowerCase()
    );
    return fv?.value || "";
  };

  const toggleSelectedCard = (cardId) => {
    setSelectedCardIds((prev) =>
      prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId]
    );
  };

  const bairroOptions = React.useMemo(() => {
    const set = new Set();
    prospects.forEach((card) => {
      const bairro = parseBairro(getFieldValue(card, "Endereço"));
      if (bairro) set.add(bairro);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [prospects]);

  const displayedProspects = React.useMemo(() => {
    if (!bairroFilter) return prospects;
    return prospects.filter((card) => parseBairro(getFieldValue(card, "Endereço")) === bairroFilter);
  }, [prospects, bairroFilter]);

  const allVisibleSelected =
    displayedProspects.length > 0 && displayedProspects.every((card) => selectedCardIds.includes(card.id));
  const someVisibleSelected =
    !allVisibleSelected && displayedProspects.some((card) => selectedCardIds.includes(card.id));

  const toggleSelectAllVisible = () => {
    const visibleIds = displayedProspects.map((card) => card.id);
    setSelectedCardIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const handleOpenRoute = () => {
    const selectedCards = prospects.filter((card) => selectedCardIds.includes(card.id));
    const withoutAddress = selectedCards.filter((card) => !getFieldValue(card, "Endereço"));
    if (withoutAddress.length > 0) {
      toast.warn(
        `${withoutAddress.length} selecionado(s) sem campo "Endereço" preenchido - serão ignorados na rota.`
      );
    }
    setRouteModalOpen(true);
  };

  return (
    <Box style={{ width: "100%", maxWidth: 1300, margin: "0 auto" }}>
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            icon={<GroupsIcon fontSize="small" />}
            color="#1565c0"
            value={kpis.total}
            label="Total no período"
            sublabel="prospects encontrados"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            icon={<PersonAddIcon fontSize="small" />}
            color="#00838f"
            value={kpis.novos}
            label="Novos"
            sublabel="início do relacionamento"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <KpiCard
            icon={<ForumIcon fontSize="small" />}
            color="#f9a825"
            value={kpis.emAndamento}
            label="Em andamento"
            sublabel="oportunidades abertas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<EmojiEventsIcon fontSize="small" />}
            color="#2e7d32"
            value={kpis.ganhos}
            label="Ganhos"
            sublabel="negociações convertidas"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            icon={<TrendingUpIcon fontSize="small" />}
            color="#6a1b9a"
            value={`${kpis.conversao}%`}
            label="Conversão"
            sublabel="ganhos entre resultados"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" style={{ padding: 16, height: "100%" }}>
            <Typography variant="overline" color="textSecondary">
              Jornada comercial
            </Typography>
            <Typography variant="h6" gutterBottom>
              Funil de prospects
            </Typography>
            {funnel.map((f) => (
              <Box key={f.phaseId} display="flex" alignItems="center" mb={1}>
                <Box style={{ width: 130, fontSize: "0.8em" }} noWrap component="span">
                  {f.name}
                </Box>
                <Box flex={1} style={{ background: "#eee", borderRadius: 4, height: 18, marginRight: 8 }}>
                  <Box
                    style={{
                      width: `${(f.count / funnelMax) * 100}%`,
                      background: f.color || "#9e9e9e",
                      height: "100%",
                      borderRadius: 4,
                      minWidth: f.count > 0 ? 4 : 0,
                    }}
                  />
                </Box>
                <Box style={{ width: 24, textAlign: "right", fontSize: "0.85em" }}>{f.count}</Box>
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" style={{ padding: 16, height: "100%" }}>
            <Typography variant="overline" color="textSecondary">
              Situação atual
            </Typography>
            <Typography variant="h6" gutterBottom>
              Distribuição dos resultados
            </Typography>
            <Box display="flex" alignItems="center">
              <Box style={{ position: "relative", width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData.length ? donutData : [{ name: "Sem dados", value: 1, key: "empty" }]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={70}
                      strokeWidth={0}
                    >
                      {(donutData.length ? donutData : [{ key: "empty" }]).map((entry) => (
                        <Cell
                          key={entry.key}
                          fill={donutData.length ? DISTRIBUTION_COLORS[entry.key] : "#e0e0e0"}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="h5" style={{ fontWeight: "bold" }}>
                    {distributionTotal}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    prospects
                  </Typography>
                </Box>
              </Box>
              <Box ml={2}>
                {["emAndamento", "ganhos", "perdidos"].map((key) => (
                  <Box key={key} display="flex" alignItems="center" mb={0.5}>
                    <Box
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: DISTRIBUTION_COLORS[key],
                        marginRight: 6,
                      }}
                    />
                    <Typography variant="body2">
                      {DISTRIBUTION_LABELS[key]} {distribution[key]}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" style={{ padding: 16 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box>
            <Typography variant="overline" color="textSecondary">
              Gestão comercial
            </Typography>
            <Typography variant="h6">Prospects cadastrados</Typography>
          </Box>
          <Box display="flex" gridGap={8}>
            {selectedCardIds.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<DirectionsIcon />}
                onClick={handleOpenRoute}
              >
                Rotear {selectedCardIds.length} selecionados
              </Button>
            )}
            {displayedProspects.length > 0 && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<MapIcon />}
                onClick={() => setMapModalOpen(true)}
              >
                Ver todos no mapa
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<SearchIcon />}
              onClick={onGoToSearch}
            >
              Buscar empresas
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddProspect}
            >
              Novo prospect
            </Button>
          </Box>
        </Box>

        {isMobile && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((v) => !v)}
            style={{ marginBottom: 8 }}
            fullWidth
          >
            Filtros {filtersOpen ? "▲" : "▼"}
          </Button>
        )}
        <Grid container spacing={1} style={{ marginBottom: 12, display: isMobile && !filtersOpen ? "none" : "flex" }}>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              variant="outlined"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              label="Responsável"
              size="small"
              fullWidth
              variant="outlined"
              value={responsavelId}
              onChange={(e) => setResponsavelId(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {(users || []).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              label="Bairro"
              size="small"
              fullWidth
              variant="outlined"
              value={bairroFilter}
              onChange={(e) => setBairroFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {bairroOptions.map((bairro) => (
                <MenuItem key={bairro} value={bairro}>
                  {bairro}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Data inicial"
              type="date"
              size="small"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              label="Data final"
              type="date"
              size="small"
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <TextField
              label="Pesquisar"
              placeholder="Nome, empresa, contato..."
              size="small"
              fullWidth
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
        </Grid>

        {isMobile ? (
          <Box>
            {displayedProspects.length > 0 && (
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <Button size="small" onClick={toggleSelectAllVisible}>
                  {allVisibleSelected ? "Desmarcar todos" : "Selecionar todos"}
                </Button>
              </Box>
            )}
            {loadingProspects ? (
              <Box textAlign="center" py={3}>
                <Typography variant="caption" color="textSecondary">Carregando...</Typography>
              </Box>
            ) : displayedProspects.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Chip label="Nenhum prospect encontrado com esses filtros" />
              </Box>
            ) : (
              displayedProspects.map((card) => {
                const phase = phaseById[card.phaseId];
                const displayName = card.ticket?.contact?.name || card.title || "(sem nome)";
                const empresa = getFieldValue(card, "Empresa");
                const bairro = parseBairro(getFieldValue(card, "Endereço"));
                const selected = selectedCardIds.includes(card.id);
                return (
                  <Paper
                    key={card.id}
                    variant="outlined"
                    style={{
                      padding: 12,
                      marginBottom: 8,
                      cursor: "pointer",
                      borderColor: selected ? theme.palette.primary.main : undefined,
                      backgroundColor: selected ? `${theme.palette.primary.main}0D` : undefined,
                    }}
                    onClick={() => onOpenCard(card)}
                  >
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                      <Checkbox
                        size="small"
                        checked={selected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelectedCard(card.id)}
                        style={{ padding: 4, marginTop: -4 }}
                      />
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2" style={{ fontWeight: 700 }} noWrap>
                          {displayName}
                        </Typography>
                        {empresa && (
                          <Typography variant="caption" color="textSecondary" display="block" noWrap>
                            {empresa}
                          </Typography>
                        )}
                        <Box display="flex" alignItems="center" style={{ gap: 6, flexWrap: "wrap" }} mt={0.5}>
                          <Chip
                            size="small"
                            label={phase?.name || "-"}
                            style={{
                              backgroundColor: `${phase?.color || "#9e9e9e"}33`,
                              borderColor: phase?.color || "#9e9e9e",
                            }}
                            variant="outlined"
                          />
                          {bairro && (
                            <Typography variant="caption" color="textSecondary">{bairro}</Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography variant="caption" color="textSecondary" style={{ flexShrink: 0 }}>
                        {moment(card.createdAt).format("DD/MM")}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Box>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  onChange={toggleSelectAllVisible}
                />
              </TableCell>
              <TableCell>Cliente / Empresa</TableCell>
              <TableCell>Bairro</TableCell>
              <TableCell>Fase</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Criado em</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingProspects ? (
              <TableRowSkeleton columns={6} />
            ) : (
              displayedProspects.map((card) => {
                const phase = phaseById[card.phaseId];
                const displayName = card.ticket?.contact?.name || card.title || "(sem nome)";
                const empresa = getFieldValue(card, "Empresa");
                const bairro = parseBairro(getFieldValue(card, "Endereço"));
                const selected = selectedCardIds.includes(card.id);
                return (
                  <TableRow
                    key={card.id}
                    hover
                    selected={selected}
                    style={{ cursor: "pointer" }}
                    onClick={() => onOpenCard(card)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={selected} onChange={() => toggleSelectedCard(card.id)} />
                    </TableCell>
                    <TableCell>
                      <b>{displayName}</b>
                      {empresa && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {empresa}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{bairro || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={phase?.name || "-"}
                        style={{
                          backgroundColor: `${phase?.color || "#9e9e9e"}33`,
                          borderColor: phase?.color || "#9e9e9e",
                        }}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{card.responsavel?.name || "-"}</TableCell>
                    <TableCell>{moment(card.createdAt).format("DD/MM/YYYY")}</TableCell>
                  </TableRow>
                );
              })
            )}
            {!loadingProspects && displayedProspects.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Chip label="Nenhum prospect encontrado com esses filtros" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        )}
      </Paper>

      {routeModalOpen && (
        <RouteModal
          open={routeModalOpen}
          onClose={() => setRouteModalOpen(false)}
          boardId={boardId}
          points={prospects
            .filter((card) => selectedCardIds.includes(card.id))
            .map((card) => ({
              cardId: card.id,
              label: card.ticket?.contact?.name || card.title || getFieldValue(card, "Empresa") || `Card ${card.id}`,
              address: getFieldValue(card, "Endereço"),
            }))
            .filter((p) => p.address)}
        />
      )}

      {mapModalOpen && (
        <MapSelectionModal
          open={mapModalOpen}
          onClose={() => setMapModalOpen(false)}
          boardId={boardId}
          points={displayedProspects
            .map((card) => ({
              cardId: card.id,
              label: card.ticket?.contact?.name || card.title || getFieldValue(card, "Empresa") || `Card ${card.id}`,
              address: getFieldValue(card, "Endereço"),
            }))
            .filter((p) => p.address)}
        />
      )}
    </Box>
  );
};

export default CommercialDashboard;
